import React, { useCallback, useState } from 'react';
import {
  Container,
  Header,
  Button,
  Table,
  Box,
  SpaceBetween,
  ButtonDropdown,
  Toggle,
  Alert,
} from '@cloudscape-design/components';
import { QuestionFormParams } from '../QuestionFormWizard';
import { FileData } from '../../textEditor/TextEditor';
import { SubQuestion } from 'src/types/helios/Question';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import SubQuestionSidePanel from '../SubQuestionSidePanel';
import WarningModal from '../../WarningModal';
import Tooltip from 'src/components/Tooltip';
import { AuditTooltipMessages } from 'src/contstant/commonLabels';
import { FileMetadata } from '../../FileDisplay';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';

interface SubQuestionsStepProps {
  formData: QuestionFormParams;
  setFormData: React.Dispatch<React.SetStateAction<QuestionFormParams>>;
  fileList: Record<string, FileData[]>;
  setFileList: React.Dispatch<React.SetStateAction<Record<string, FileData[]>>>;
  documentFiles?: Record<string, File[]>;
  setDocumentFiles?: React.Dispatch<React.SetStateAction<Record<string, File[]>>>;
  documentFilesMetadata?: Record<string, FileMetadata[]>;
  setDocumentFilesMetadata?: React.Dispatch<
    React.SetStateAction<Record<string, FileMetadata[]>>
  >;
}

const SubQuestions: React.FC<SubQuestionsStepProps> = ({
  formData,
  setFormData,
  fileList,
  setFileList,
  documentFiles,
  setDocumentFiles,
  documentFilesMetadata,
  setDocumentFilesMetadata,
}) => {
  // Feature flags
  const { enableEnhancedResponseTypes } = useFeatureFlags();

  // Split panel context
  const { setContent, setTitle, closeView } = useSplitPanel();

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number>(-1);
  // ERT: Info message when toggling conditional on/off
  const [conditionalInfoMessage, setConditionalInfoMessage] = useState<string | null>(
    null,
  );
  const [conditionalAlertType, setConditionalAlertType] = useState<'info' | 'error'>(
    'info',
  );

  // Handle conditional toggle change
  const handleConditionalToggleChange = useCallback(
    (checked: boolean) => {
      // Close any open side panel to prevent stale state
      closeView();

      if (checked && formData.subQuestions && formData.subQuestions.length > 0) {
        // Block if any P/F subs exist — conditional only allows data-collection types
        const passFalSubs = formData.subQuestions.filter(
          (sq) => !sq.responseType || sq.responseType === 'PASS_FAIL',
        );
        if (passFalSubs.length > 0) {
          setConditionalAlertType('error');
          setConditionalInfoMessage(
            'Cannot enable conditional display: Remove or change Pass/Fail sub-questions to data-collection types before enabling conditional.',
          );
          return;
        }

        // Turning ON with existing subs: default unset ones to BOTH, preserve configured values
        const subsWithoutShowOn = formData.subQuestions.filter(
          (sq) => !sq.showWhenParentIs,
        );
        const updatedSubQuestions = formData.subQuestions.map((sq) => ({
          ...sq,
          showWhenParentIs: sq.showWhenParentIs || 'BOTH',
        }));
        setFormData((prev) => ({
          ...prev,
          hasConditionalSubQuestions: true,
          subQuestions: updatedSubQuestions,
        }));
        setConditionalInfoMessage(
          subsWithoutShowOn.length > 0
            ? 'Sub-questions without a "Show on" setting have been defaulted to "Both". Edit individual sub-questions to change when they appear.'
            : 'Sub-questions will now appear based on their configured "Show on" setting.',
        );
        setConditionalAlertType('info');
      } else {
        setFormData((prev) => ({
          ...prev,
          hasConditionalSubQuestions: checked,
        }));
        // Only show message when turning off and there are existing subs
        if (!checked && formData.subQuestions && formData.subQuestions.length > 0) {
          setConditionalInfoMessage(
            'All sub-questions will always appear during the audit regardless of the parent answer.',
          );
          setConditionalAlertType('info');
        } else {
          setConditionalInfoMessage(null);
        }
      }
    },
    [formData.subQuestions, setFormData, closeView],
  );

  // Handle opening add side panel
  const handleOpenAddSidePanel = useCallback(() => {
    setTitle('Add Sub Question');
    setContent(
      <SubQuestionSidePanel
        mode="add"
        formData={formData}
        setFormData={setFormData}
        fileList={fileList}
        setFileList={setFileList}
        documentFiles={documentFiles}
        setDocumentFiles={setDocumentFiles}
        documentFilesMetadata={documentFilesMetadata}
        setDocumentFilesMetadata={setDocumentFilesMetadata}
        onClose={closeView}
      />,
    );
  }, [
    formData,
    setFormData,
    fileList,
    setFileList,
    documentFiles,
    setDocumentFiles,
    documentFilesMetadata,
    setDocumentFilesMetadata,
    setTitle,
    setContent,
    closeView,
  ]);

  // Handle opening edit side panel
  const handleOpenEditSidePanel = useCallback(
    (index: number) => {
      setTitle('Edit Sub Question');
      setContent(
        <SubQuestionSidePanel
          mode="edit"
          editingIndex={index}
          formData={formData}
          setFormData={setFormData}
          fileList={fileList}
          setFileList={setFileList}
          documentFiles={documentFiles}
          setDocumentFiles={setDocumentFiles}
          documentFilesMetadata={documentFilesMetadata}
          setDocumentFilesMetadata={setDocumentFilesMetadata}
          onClose={closeView}
        />,
      );
    },
    [
      formData,
      setFormData,
      fileList,
      setFileList,
      documentFiles,
      setDocumentFiles,
      documentFilesMetadata,
      setDocumentFilesMetadata,
      setTitle,
      setContent,
      closeView,
    ],
  );

  // Handle delete confirmation
  const handleDeleteClick = useCallback((index: number) => {
    setDeleteIndex(index);
    setShowDeleteModal(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(() => {
    if (deleteIndex >= 0 && formData.subQuestions) {
      const updatedSubQuestions = formData.subQuestions.filter(
        (_, index) => index !== deleteIndex,
      );
      setFormData((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
    }
    setShowDeleteModal(false);
    setDeleteIndex(-1);
    closeView();
  }, [deleteIndex, formData.subQuestions, setFormData, closeView]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteIndex(-1);
  }, []);

  return (
    <Container
      data-testid="sub-questions-container"
      header={
        <Header
          data-testid="sub-questions-header"
          actions={
            <Button
              data-testid="add-sub-question-button"
              iconName="add-plus"
              onClick={handleOpenAddSidePanel}
            >
              Add Sub Question
            </Button>
          }
          info={<Tooltip content={AuditTooltipMessages.SUB_QUESTION} />}
          variant="h3"
        >
          Sub question & Sub Task
        </Header>
      }
    >
      <SpaceBetween size="m">
        {enableEnhancedResponseTypes && (
          <Box
            data-testid="conditional-toggle-container"
            padding={{ vertical: 's', horizontal: 'm' }}
            variant="div"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #e9ebed',
                borderRadius: '8px',
                padding: '12px 16px',
              }}
            >
              <div>
                <Box variant="p" fontWeight="bold" margin={{ bottom: 'xxs' }}>
                  Show sub-questions based on the answer?{' '}
                  <Box
                    variant="span"
                    display="inline"
                    fontSize="body-s"
                    fontWeight="normal"
                    padding={{ horizontal: 'xs' }}
                  >
                    <span
                      style={{
                        backgroundColor: formData.hasConditionalSubQuestions
                          ? '#0972d3'
                          : '#5f6b7a',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      {formData.hasConditionalSubQuestions ? 'On' : 'Off'}
                    </span>
                  </Box>
                </Box>
                <Box variant="small" color="text-body-secondary">
                  {formData.hasConditionalSubQuestions
                    ? "Each sub-question appears only for the Pass/Fail answer you choose. Set each one's Show on to Pass, Fail, or Both."
                    : 'Every sub-question always appears during the audit, regardless of the parent answer.'}
                </Box>
              </div>
              <Toggle
                data-testid="conditional-sub-questions-toggle"
                checked={formData.hasConditionalSubQuestions ?? false}
                onChange={({ detail }) => handleConditionalToggleChange(detail.checked)}
              />
            </div>
          </Box>
        )}

        {enableEnhancedResponseTypes && conditionalInfoMessage && (
          <Alert
            data-testid="conditional-toggle-info"
            type={conditionalAlertType}
            dismissible
            onDismiss={() => setConditionalInfoMessage(null)}
          >
            {conditionalInfoMessage}
          </Alert>
        )}

        {formData.subQuestions && formData.subQuestions.length > 0 ? (
          <Table
            data-testid="sub-questions-table"
            variant="borderless"
            columnDefinitions={[
              {
                id: 'title',
                header: 'Sub-question Title',
                maxWidth: 700,
                cell: (item: SubQuestion) => item.title || '-',
              },
              ...(enableEnhancedResponseTypes
                ? [
                    {
                      id: 'responseType',
                      header: 'Response Type',
                      maxWidth: 150,
                      cell: (item: SubQuestion) => {
                        const type = item.responseType || 'PASS_FAIL';
                        const labels: Record<string, string> = {
                          PASS_FAIL: 'Pass / Fail',
                          TEXT: 'Text Input',
                          NUMERIC: 'Numeric Input',
                          SINGLE_SELECT: 'Single Select',
                          MULTI_SELECT: 'Multi Select',
                          YES_NO: 'Yes / No',
                          USER_ALIAS: 'Employee Login',
                        };
                        return labels[type] || type;
                      },
                    },
                    ...(formData.hasConditionalSubQuestions
                      ? [
                          {
                            id: 'showOn',
                            header: 'Show When Parent Is',
                            maxWidth: 150,
                            cell: (item: SubQuestion) => {
                              const value = item.showWhenParentIs || 'BOTH';
                              if (value === 'BOTH') return 'Both (Pass or Fail)';
                              if (value === 'PASS') return 'Pass';
                              if (value === 'FAIL') return 'Fail';
                              return value;
                            },
                          },
                        ]
                      : []),
                  ]
                : []),
              {
                id: 'hasTask',
                header: 'Has Task',
                maxWidth: 100,
                cell: (item: SubQuestion) => (item.taskDetails ? 'Yes' : 'No'),
              },
              {
                id: 'actions',
                header: 'Actions',
                cell: (item: SubQuestion) => {
                  const index =
                    formData.subQuestions?.findIndex(
                      (q) => q.title === item.title && q.instruction === item.instruction,
                    ) ?? -1;
                  return (
                    <ButtonDropdown
                      data-testid="action-button-dropdown"
                      expandToViewport={true}
                      items={[
                        {
                          text: 'Edit',
                          id: 'edit',
                        },
                        {
                          text: 'Delete',
                          id: 'delete',
                        },
                      ]}
                      variant="icon"
                      onItemClick={({ detail }) => {
                        if (detail.id === 'edit') {
                          handleOpenEditSidePanel(index);
                        }
                        if (detail.id === 'delete') {
                          handleDeleteClick(index);
                        }
                      }}
                    />
                  );
                },
              },
            ]}
            items={formData.subQuestions}
            trackBy={(item: SubQuestion) => `${item.title}-${item.orderIndex}`}
            empty={
              <Box
                data-testid="empty-sub-questions-message"
                textAlign="center"
                color="text-status-inactive"
                padding={{ vertical: 'xs' }}
              >
                No sub questions added
              </Box>
            }
          />
        ) : (
          <Box
            data-testid="no-sub-questions-message"
            textAlign="center"
            color="text-status-inactive"
            padding={{ vertical: 'l' }}
          >
            No sub questions added
          </Box>
        )}
      </SpaceBetween>

      <WarningModal
        data-testid="delete-sub-question-modal"
        visible={showDeleteModal}
        title="Delete Sub Question"
        message="Are you sure you want to delete this sub question? "
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Container>
  );
};

export default SubQuestions;
